import React, { useState, useRef, useEffect } from 'react';
import { Nav, NavItem, NavLink, Row, Col } from 'reactstrap';
import classnames from 'classnames';
import ReactToPrint from 'react-to-print';
import { pasteValue } from '../../Reusable/OnPasteValue';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import moment from 'moment';
import LottieLoader from "../../LottieLoader";
import lottieLoaderFile from "../../../files/animation/loading.json";
import lottieEmptyFile from "../../../files/animation/empty_new.json";
import { useFetch2 } from "components/utils/authenticatedHook";
import { Link } from "react-router-dom";
import Select from 'react-select';
import PrintableLACERiskTable from './PrintableLACERiskTable';
import { useSelector } from "react-redux";
import { ROLES } from '../../../constants/role.constant';
import useResponsiveView from '../../../hooks/useResponsiveView';

const LACERiskManagement = () => {
  const [{ response, error, isLoading }, doFetch, sendError] = useFetch2(null);
  const [{ response: exportData, error: exportError, isLoading: exportLoading }, doFetch2] = useFetch2(null);
  const [{ response: printResponse, error: printError, isLoading: printLoading }, doPrintFetch] = useFetch2(null);
  const [{ response: careproviderStaffs, error: careproviderStaffsError, isLoading: careproviderStaffsLoading }, doFetchCareproviderStaff] = useFetch2(null);
  const { userDetails } = useSelector(state => state.user);
  const view = useResponsiveView();
  const { data = [], limit = 25, page = 0, totalCount = 0 } = response ? response.data : {};
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('');
  const [selectedRiskLevels, setSelectedRiskLevels] = useState([]);
  const [selectedCareproviders, setSelectedCareproviders] = useState([]);
  const [selectedStaffs, setSelectedStaffs] = useState([]);
  const [isPrinting, setIsPrinting] = useState(false);
  const [printData, setPrintData] = useState(null);
  const [printApiLoading, setPrintApiLoading] = useState(false);
  const searchTimeoutRef = useRef(null);
  const componentRef = useRef(null);
  const printRef = useRef(null);


  const riskOptions = [
    { value: 'low', label: 'Low Risk (0-4)' },
    { value: 'moderate', label: 'Moderate Risk (5-9)' },
    { value: 'high', label: 'High Risk (10+)' }
  ];

  // Transform careprovider data for react-select
  const transformCareproviderOptions = (careproviders) => {
    return careproviders?.map(careprovider => ({
      value: careprovider._id,
      label: `${careprovider.fname} ${careprovider.lname}`
    })) || [];
  };

  // Transform staff data for react-select
  const transformStaffOptions = (staffs) => {
    return staffs?.map(staff => ({
      value: staff._id,
      label: `${staff.fname} ${staff.lname}`
    })) || [];
  };
  const customTotal = (from, to, size) => (
    <span className="react-bootstrap-table-pagination-total">
      {"   "}Showing {from} to {to} of {size} Results
    </span>
  );
  const options = {
    page: parseInt(page || 0),
    sizePerPage: parseInt(limit || 0),
    totalSize: parseInt(totalCount || 0),
    firstPageText: "First",
    prePageText: "Back",
    nextPageText: "Next",
    lastPageText: "Last",
    nextPageTitle: "First page",
    prePageTitle: "Pre page",
    firstPageTitle: "Next page",
    lastPageTitle: "Last page",
    hideSizePerPage: parseInt(totalCount) == 0 ? true : false, // Hide the sizePerPage dropdown always
    hidePageListOnlyOnePage: true, // Hide the pagination list when only one page
    showTotal: parseInt(totalCount) == 0 ? false : true,
    paginationTotalRenderer: customTotal,
    sizePerPageList: [
      {
        text: "25",
        value: 25,
      },
      {
        text: "50",
        value: 50,
      },
      {
        text: "100",
        value: 100,
      },
      {
        text: "200",
        value: 200,
      },
    ], // A numeric array is also available. the purpose of above example is custom the text
  };

  const handleSearch = (event) => {
    const value = event.target.value;
    setSearchTerm(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      const query = {
        search: value || '',
        sortBy: sortField || '',
        sortOrder: sortOrder || '',
        page: page || 1,
        limit: limit || 25,
        riskLevels: selectedRiskLevels.map(level => level.value).join(',')
      };
      fetchLaceRiskScores(query);
    }, 500);
  };

  const handleRiskLevelChange = (selectedOptions) => {
    setSelectedRiskLevels(selectedOptions || []);
    const query = {
      search: searchTerm || '',
      sortBy: sortField || '',
      sortOrder: sortOrder || '',
      page: page || 1,
      limit: limit || 25,
      riskLevels: (selectedOptions || []).map(level => level.value).join(','),
    };
    fetchLaceRiskScores(query);
  };

  const handleCareproviderChange = (selectedOptions) => {
    setSelectedCareproviders(selectedOptions || []);
    const query = {
      search: searchTerm || '',
      sortBy: sortField || '',
      sortOrder: sortOrder || '',
      page: page || 1,
      limit: limit || 25,
      careprovider: selectedOptions?.value || null,
      riskLevels: (selectedRiskLevels || []).map(level => level.value).join(',')
    };
    fetchLaceRiskScores(query);
  };

  const handleStaffChange = (selectedOptions) => {
    setSelectedStaffs(selectedOptions || []);
    const query = {
      search: searchTerm || '',
      sortBy: sortField || '',
      sortOrder: sortOrder || '',
      page: page || 1,
      limit: limit || 25,
      staff: selectedOptions?.value || null,
      riskLevels: (selectedRiskLevels || []).map(level => level.value).join(',')
    };
    fetchLaceRiskScores(query);
  };

  const rowStyle = (row, rowIndex) => {
    return {
      backgroundColor: rowIndex % 2 === 0 ? '#f8f9fa' : '#ffffff',
    };
  };

  const fetchLaceRiskScores = (query) => {
    doFetch({
      url: "/get-all-lace-risk",
      method: "GET",
      disableWait: true,
      query: query,
    })
  }

  const handleExport = () => {
    const query = {
      riskLevels: selectedRiskLevels.map(level => level.value).join(','),
      staff: selectedStaffs?.value || null,
      careprovider: selectedCareproviders?.value || null
    };
    doFetch2({
      url: "/export-all-lace-risk",
      method: "GET",
      disableWait: true,
      query
    });
  }

  useEffect(() => {
    fetchLaceRiskScores({ page: 1, limit: 25 });
    if (userDetails.role === ROLES.admin) {
      doFetchCareproviderStaff({
        url: "/scheduled-appointment-staff-careprovider",
        method: "GET",
        disableWait: true,
      })
    }
  }, []);

  // Handle export response
  useEffect(() => {
    if (exportData && exportData.data && exportData.data.csvContent) {
      // Create blob from CSV content
      const blob = new Blob([exportData.data.csvContent], { type: 'text/csv;charset=utf-8;' });

      // Create blob URL using window.URL.createObjectURL
      const blobUrl = window.URL.createObjectURL(blob);

      // Create download link
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = exportData.data.filename || 'lace_risk_scores.csv';

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    }
  }, [exportData]);

  // Handle print response
  useEffect(() => {
    if (printApiLoading && printResponse && printResponse.data && printResponse.data.data) {
      setPrintData(printResponse.data.data);
      setPrintApiLoading(false);
      // Trigger print
      setTimeout(() => {
        if (printRef.current) {
          printRef.current.handlePrint();
        }
      }, 400);
    }
  }, [printResponse]);

  return (
    <div className="row">
      <div className="col-12 grid-margin">
        <div className="card">
          <div className="card-body project-tab pt-2">
            <Nav tabs className="justify-content-center">
              <NavItem>
                <NavLink
                  className={classnames({
                    active: true
                  })}
                >
                  LACE RISK SCORE
                </NavLink>
              </NavItem>
            </Nav>
            <div className={`d-flex ${view === 'mobile' || view === 'tablet' ? 'flex-column' : 'flex-row'} w-100 align-items-stretch align-items-lg-center mb-3 mt-3`} style={{ gap: 12 }}>
              <input
                type="text"
                className="form-control input-date-filter remove-bottom-margin mb-1"
                id="exampleInputName1Test"
                style={{
                  fontSize: "16px",
                  borderRadius: "8px",
                  boxShadow: "none",
                  height: "45px",
                  width: '100%',
                  minWidth: view === 'mobile' || view === 'tablet' ? 'auto' : '200px',
                  flex: view === 'mobile' || view === 'tablet' ? 'none' : '1'
                }}
                placeholder="Search patient, lace risk score, organization, created-by..."
                onChange={handleSearch} // Call handleChange on every input change
                onPaste={(e) => {
                  e.preventDefault();
                  const event = pasteValue(e);
                  handleSearch(event);
                }}
              />
              <div style={{ width: '100%', flex: view === 'mobile' || view === 'tablet' ? 'none' : '1', minWidth: view === 'mobile' || view === 'tablet' ? 'auto' : '180px' }}>
                <Select
                  isMulti
                  options={riskOptions}
                  value={selectedRiskLevels}
                  onChange={handleRiskLevelChange}
                  placeholder="Select Risk Levels"
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      height: '44px',
                      borderRadius: '8px',
                      border: '1px solid #ced4da',
                      boxShadow: 'none',
                      '&:hover': {
                        borderColor: '#adb5bd'
                      }
                    }),
                    multiValue: (provided) => ({
                      ...provided,
                      backgroundColor: '#e9ecef'
                    }),
                    multiValueLabel: (provided) => ({
                      ...provided,
                      color: '#495057'
                    }),
                    multiValueRemove: (provided) => ({
                      ...provided,
                      color: '#6c757d',
                      '&:hover': {
                        backgroundColor: '#dee2e6',
                        color: '#495057'
                      }
                    })
                  }}
                />
              </div>


              {userDetails.role === ROLES.admin && (<div style={{ width: '100%', flex: view === 'mobile' || view === 'tablet' ? 'none' : '1', minWidth: view === 'mobile' || view === 'tablet' ? 'auto' : '180px' }}>
                <Select
                  isClearable
                  options={transformCareproviderOptions(careproviderStaffs?.data?.careprovider || [])}
                  value={selectedCareproviders}
                  onChange={handleCareproviderChange}
                  placeholder="Select Careprovider"
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      height: '44px',
                      borderRadius: '8px',
                      border: '1px solid #ced4da',
                      boxShadow: 'none',
                      '&:hover': {
                        borderColor: '#adb5bd'
                      }
                    }),
                    multiValue: (provided) => ({
                      ...provided,
                      backgroundColor: '#e9ecef'
                    }),
                    multiValueLabel: (provided) => ({
                      ...provided,
                      color: '#495057'
                    }),
                    multiValueRemove: (provided) => ({
                      ...provided,
                      color: '#6c757d',
                      '&:hover': {
                        backgroundColor: '#dee2e6',
                        color: '#495057'
                      }
                    })
                  }}
                />
              </div>)}


              {userDetails.role === ROLES.admin && (<div style={{ width: '100%', flex: view === 'mobile' || view === 'tablet' ? 'none' : '1', minWidth: view === 'mobile' || view === 'tablet' ? 'auto' : '180px' }}>
                <Select
                  isClearable
                  options={transformStaffOptions(careproviderStaffs?.data?.staffs || [])}
                  value={selectedStaffs}
                  onChange={handleStaffChange}
                  placeholder="Select Staff"
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      height: '44px',
                      borderRadius: '8px',
                      border: '1px solid #ced4da',
                      boxShadow: 'none',
                      '&:hover': {
                        borderColor: '#adb5bd'
                      }
                    }),
                    multiValue: (provided) => ({
                      ...provided,
                      backgroundColor: '#e9ecef'
                    }),
                    multiValueLabel: (provided) => ({
                      ...provided,
                      color: '#495057'
                    }),
                    multiValueRemove: (provided) => ({
                      ...provided,
                      color: '#6c757d',
                      '&:hover': {
                        backgroundColor: '#dee2e6',
                        color: '#495057'
                      }
                    })
                  }}
                />
              </div>)}
              
              <div className={`d-flex ${view === 'mobile' || view === 'tablet' ? 'flex-column' : 'flex-row'} gap-2`} style={{ flex: '0 0 auto', width: view === 'mobile' || view === 'tablet' ? '100%' : 'auto' }}>
                <button
                  className="btn btn-info print-click-cls"
                  disabled={isPrinting || printApiLoading}
                  onClick={() => {
                    const query = {
                      riskLevels: selectedRiskLevels.map(level => level.value).join(','),
                      staff: selectedStaffs?.value || null,
                      careprovider: selectedCareproviders?.value || null
                    };
                    setPrintApiLoading(true);
                    doPrintFetch({
                      url: "/print-all-lace-risk",
                      method: "GET",
                      disableWait: true,
                      query
                    });
                  }}
                  style={{ whiteSpace: 'nowrap', width: view === 'mobile' || view === 'tablet' ? '100%' : 'auto', marginBottom: view === 'mobile' || view === 'tablet' ? '8px' : '0' }}
                >
                  {printApiLoading ? (
                    <i className="mdi mdi-loading mdi-spin mr-2" style={{ fontSize: 20 }}></i>
                  ) : (
                    <i
                      className="mdi mdi-cloud-print mr-2"
                      style={{ fontSize: 20 }}
                    ></i>
                  )}
                  Print
                </button>
                <ReactToPrint
                  ref={printRef}
                  trigger={() => <div></div>}
                  content={() => componentRef.current}
                  onBeforePrint={() => {
                    setIsPrinting(true);
                    document.title = `LACE Risk Score Report`;
                  }}
                  onAfterPrint={() => {
                    setIsPrinting(false);
                    document.title = "MYVITALVIEW";
                  }}
                ></ReactToPrint>
                <button className={`btn download-click-cls ${view === 'desktop' ? 'ml-md-2' : ''}`} style={{ height: 43, color: 'white', whiteSpace: 'nowrap', width: view === 'mobile' || view === 'tablet' ? '100%' : 'auto' }} onClick={() => handleExport()} disabled={isLoading || exportLoading}>
                  {(exportLoading) ? (
                    <i className="mdi mdi-loading mdi-spin mr-2" style={{ fontSize: 20 }}></i>
                  ) : (
                    <>
                      <i className="mdi mdi-download mr-2" style={{ fontSize: 20 }}></i>
                    </>
                  )} Export to CSV
                </button>
              </div>
            </div>

            <Row className="col-sm-12 main-cls-modal remove-table-padding">
              <Col sm="12" className="mt-12" style={{ overflowX: 'auto', paddingLeft : '0px', paddingRight : '-1px' }}>
                <div className='scrollbar-table-cls'>
                  <BootstrapTable
                    remote={{
                      sort: false,
                      pagination: true,
                      filter: false,
                      search: false,
                    }}
                    wrapperClasses="table-responsive"
                    keyField="_id"
                    noDataIndication={() => {
                      return (
                        <div>
                          {isLoading && (
                            <LottieLoader
                              loadLoop={true}
                              autoplayLoop={true}
                              source={lottieLoaderFile}
                              Lheight={150}
                              Lwidth={150}
                              subTitle={"Please Wait ..."}
                            />
                          )}
                          {!isLoading && (
                            <LottieLoader
                              loadLoop={true}
                              autoplayLoop={true}
                              source={lottieEmptyFile}
                              Lheight={300}
                              Lwidth={300}
                              title={"Sorry! No data found"}
                              subTitle={"We can't find any LACE risk score data."}
                            />
                          )}
                        </div>
                      );
                    }}
                    data={isLoading ? [] : data}
                    columns={[
                      {
                        dataField: "patientName",
                        text: "Patient Name",
                        sort: true,
                        formatter: (cell) => <p style={{ marginTop: '11px' }} className="d-flex justify-content-center align-items-center">{cell ? cell : "-"}</p>,
                        headerStyle: (colum, colIndex) => {
                          return { width: '20%', textAlign: 'center' };
                        },
                      },
                      {
                        dataField: "organization",
                        text: "Organization",
                        sort: true,
                        formatter: (cell) => <p style={{ marginTop: '11px' }} className="d-flex justify-content-center align-items-center">{cell ? cell : "-"}</p>,
                        headerStyle: (colum, colIndex) => {
                          return { width: '20%', textAlign: 'center' };
                        },
                      },
                      {
                        dataField: "lastEntry.createdByName",
                        text: "Created by",
                        sort: true,
                        formatter: (cell) => <p style={{ marginTop: '11px' }} className="d-flex justify-content-center align-items-center">{cell ? cell : "-"}</p>,
                        headerStyle: (colum, colIndex) => {
                          return { width: '15%', textAlign: 'center' };
                        },
                      },
                      {
                        dataField: "lastEntry.modifyDate",
                        text: "Last Update Date",
                        sort: true,
                        formatter: (cell, row) => (
                          <p style={{ marginTop: '11px' }} className="d-flex justify-content-center align-items-center">
                            {row.lastEntry && row.lastEntry.modifyDate ? moment(row.lastEntry.modifyDate).format("MMMM DD, YYYY hh:mm A") : "-"}
                          </p>
                        ),
                        headerStyle: (colum, colIndex) => {
                          return { width: '15%', textAlign: 'center' };
                        },
                      },
                      {
                        dataField: "lastEntry.totalScore",
                        text: "Last LACE Score",
                        sort: true,
                        formatter: (cell) => {
                          if (!cell && cell !== 0) return <p>-</p>;
                          let risk = '';
                          let badgeStyle = {};
                          if (cell <= 4) {
                            risk = 'Low';
                            badgeStyle = { backgroundColor: '#28a745', color: '#fff' };
                          } else if (cell <= 9) {
                            risk = 'Moderate';
                            badgeStyle = { backgroundColor: '#ffc107', color: '#212529' };
                          } else {
                            risk = 'High';
                            badgeStyle = { backgroundColor: '#dc3545', color: '#fff' };
                          }
                          return (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                              <p style={{ margin: 0, fontWeight: 'bold' }}>{cell}</p>
                              <span
                                style={{
                                  ...badgeStyle,
                                  padding: '4px 8px',
                                  borderRadius: '12px',
                                  fontSize: '0.75rem',
                                  fontWeight: 'bold',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                  border: '1px solid rgba(255,255,255,0.2)'
                                }}
                              >
                                {risk}
                              </span>
                            </div>
                          );
                        },
                        headerStyle: (colum, colIndex) => {
                          return { width: '10%', textAlign: 'center' };
                        },
                      },
                      {
                        dataField: "lastEntry.lastModifyByName",
                        text: "Last LACE Risk Updated By",
                        formatter: (cell, row) => {
                          if (cell) {
                            return (
                              <div className="d-flex justify-content-center align-items-center">
                                <p style={{ marginBottom: '2px' }}>{cell ? cell : "-"}</p>
                              </div>
                            )
                          } else {
                            return <p className="d-flex justify-content-center align-items-center">{cell ? cell : "-"}</p>
                          }
                        },
                        headerStyle: (colum, colIndex) => {
                          return { width: '15%', textAlign: 'center' };
                        },
                      },
                      {
                        dataField: "action",
                        text: "Action",
                        sort: false,
                        headerStyle: (colum, colIndex) => {
                          return { width: "5%", textAlign: "center" };
                        },
                        formatter: (cell, row, rowIndex) => {
                          return (
                            <div className="d-flex justify-content-center align-items-center">
                              <Link
                                to={`/dashboard/${row.lastEntry.patientFName
                                  .replace(/\s+/g, "-").replace(/[^a-zA-Z0-9 ]/g, '')
                                  .toLowerCase()}-${row.lastEntry.patientLName.replace(/\s+/g, "-").replace(/[^a-zA-Z0-9 ]/g, '').toLowerCase()}/${row.lastEntry.patientId
                                  }?tab=14`}
                                className="btn more-btn"
                              >
                                <i className="mdi mdi-account-card-details"></i> More Details
                              </Link>
                            </div>
                          );
                        },
                      },
                    ]}
                    expandRow={{
                      renderer: (row) => (
                        <div style={{
                          padding: '20px',
                          backgroundColor: '#ffffff',
                          borderRadius: '8px',
                          border: '1px solid #dee2e6',
                          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                          margin: '15px'
                        }}>
                          <h6 style={{
                            marginBottom: '15px',
                            color: '#495057',
                            fontWeight: 'bold',
                            fontSize: '1.1rem'
                          }}>All LACE Risk</h6>
                          <BootstrapTable
                            keyField="_id"
                            data={row.entries || []}
                            columns={[
                              {
                                dataField: "createdByName",
                                text: "Created by",
                                formatter: (cell) => <p style={{ margin: 0, fontSize: '0.9rem' }} className="d-flex justify-content-center align-items-center">{cell ? cell : "-"}</p>,
                                headerStyle: () => {
                                  return { textAlign: 'center' };
                                },
                              },
                              {
                                dataField: "createdAt",
                                text: "Created Date",
                                formatter: (cell) => <p style={{ margin: 0, fontSize: '0.9rem' }} className="d-flex justify-content-center align-items-center">{cell ? moment(cell).format("MMMM DD, YYYY hh:mm A") : "-"}</p>,
                                headerStyle: () => {
                                  return { textAlign: 'center' };
                                },
                              },
                              {
                                dataField: "totalScore",
                                text: "Score",
                                formatter: (cell) => {
                                  if (!cell && cell !== 0) return <p style={{ margin: 0, fontSize: '0.9rem' }}>-</p>;
                                  let risk = '';
                                  let badgeStyle = {};
                                  if (cell <= 4) {
                                    risk = 'Low';
                                    badgeStyle = { backgroundColor: '#28a745', color: '#fff' };
                                  } else if (cell <= 9) {
                                    risk = 'Moderate';
                                    badgeStyle = { backgroundColor: '#ffc107', color: '#212529' };
                                  } else {
                                    risk = 'High';
                                    badgeStyle = { backgroundColor: '#dc3545', color: '#fff' };
                                  }
                                  return (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center' }}>
                                      <p style={{ margin: 0, fontSize: '0.9rem', fontWeight: 'bold' }}>{cell}</p>
                                      <span
                                        style={{
                                          ...badgeStyle,
                                          padding: '2px 6px',
                                          borderRadius: '10px',
                                          fontSize: '0.7rem',
                                          fontWeight: 'bold',
                                          textTransform: 'uppercase',
                                          letterSpacing: '0.3px',
                                          boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                          border: '1px solid rgba(255,255,255,0.2)'
                                        }}
                                      >
                                        {risk}
                                      </span>
                                    </div>
                                  );
                                },
                                headerStyle: () => {
                                  return { textAlign: 'center' };
                                },
                              },
                              {
                                dataField: "lastModifyByName",
                                text: "Updated By",
                                formatter: (cell, row) => {
                                  if (cell) {
                                    return (
                                      <div className="d-flex justify-content-center align-items-center">
                                        <p style={{ marginBottom: '2px', fontSize: '0.9rem' }}>{cell ? cell : "-"}</p>
                                      </div>
                                    )
                                  } else {
                                    return <p style={{ fontSize: '0.9rem' }} className="d-flex justify-content-center align-items-center">{cell ? cell : "-"}</p>
                                  }
                                },
                                headerStyle: () => {
                                  return { textAlign: 'center' };
                                },
                              },
                              {
                                dataField: "lastModifyByName",
                                text: "Updated Date",
                                formatter: (cell, row) => {
                                  if (cell) {
                                    return (
                                      <div className="d-flex justify-content-center align-items-center">
                                        <p style={{ margin: 0, fontSize: '0.9rem' }} className="d-flex justify-content-center align-items-center">
                                          {row && row.modifyDate ? moment(row.modifyDate).format("MMMM DD, YYYY hh:mm A") : "-"}
                                        </p>
                                      </div>
                                    )
                                  } else {
                                    return <p style={{ margin: 0, fontSize: '0.9rem' }} className="d-flex justify-content-center align-items-center">{cell ? cell : "-"}</p>
                                  }
                                },
                                headerStyle: () => {
                                  return { textAlign: 'center' };
                                },
                              },
                            ]}
                            condensed
                            wrapperClasses="table-responsive"
                            bordered={true}
                            rowStyle={(row, rowIndex) => ({
                              backgroundColor: rowIndex % 2 === 0 ? '#f8f9fa' : '#ffffff',
                              fontSize: '0.9rem'
                            })}
                          />
                        </div>
                      ),
                      showExpandColumn: true,
                      onlyOneExpanding: false,
                      expandByColumnOnly: false
                    }}
                    pagination={options?.page > 0 ? paginationFactory(options) : undefined}
                    bordered
                    rowStyle={rowStyle}
                    onTableChange={(type, { page, sizePerPage, sortField, sortOrder }) => {
                      if (type === 'pagination') {
                        const query = {
                          search: searchTerm || '',
                          sortBy: sortField || '',
                          sortOrder: sortOrder || '',
                          page: page || 1,
                          limit: sizePerPage || 25
                        };
                        fetchLaceRiskScores(query);
                      } else if (type === 'sort') {
                        setSortField(sortField);
                        setSortOrder(sortOrder);
                        const query = {
                          search: searchTerm || '',
                          sortBy: sortField || '',
                          sortOrder: sortOrder || '',
                          page: page || 1,
                          limit: sizePerPage || 25
                        };
                        fetchLaceRiskScores(query);
                      }
                    }}
                  />
                </div>
              </Col>
            </Row>
          </div>
        </div>
      </div>
      <div style={{ position: 'absolute', left: '-10000px', top: '-10000px', display: 'none' }}>
        <PrintableLACERiskTable ref={componentRef} data={printData} />
      </div>
    </div>
  );
};

export default LACERiskManagement;